"use client";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useState } from "react";
import { anonymousBlogService } from "~/connectors/blog.connector";
import { BlogComment } from "~/types";
import DateTimeRelative from "../DateTimeRelative";

import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface BlogCommentsProps {
  blogPostId: number;
}

export default function BlogComments({ blogPostId }: BlogCommentsProps) {
  const { data: session } = useSession();
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    authorName: "",
    authorEmail: "",
    content: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Prefill form with user data if logged in
  useEffect(() => {
    if (session?.user) {
      setFormData((prev) => ({
        ...prev,
        authorName: session.user.name || "",
        authorEmail: session.user.email || "",
      }));
    } else {
      // Reset form for non-logged-in users
      setFormData({
        authorName: "",
        authorEmail: "",
        content: "",
      });
    }
  }, [session]);

  const loadComments = useCallback(async () => {
    try {
      const response = await anonymousBlogService.getComments(blogPostId);
      setComments(response.data);
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      setLoading(false);
    }
  }, [blogPostId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.authorName.trim() || !formData.content.trim()) {
      setError("Numele și comentariul sunt obligatorii.");
      return;
    }

    if (formData.content.length > 2000) {
      setError("Comentariul nu poate depăși 2000 de caractere.");
      return;
    }

    setSubmitting(true);
    try {
      await anonymousBlogService.createComment(blogPostId, {
        authorName: formData.authorName.trim(),
        authorEmail: formData.authorEmail.trim() || undefined,
        content: formData.content.trim(),
      });

      if (session?.user) {
        setSuccess("Comentariul tău a fost publicat cu succes!");
        loadComments(); // Reload comments since it's auto-approved
      } else {
        setSuccess(
          "Comentariul tău a fost trimis și va fi publicat după moderare.",
        );
        // Don't reload comments as new comment needs approval
      }

      setFormData((prev) => ({ ...prev, content: "" }));
    } catch (error) {
      console.error("Error submitting comment:", error);
      setError(
        "A apărut o eroare la trimiterea comentariului. Te rugăm să încerci din nou.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange =
    (field: string) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h6" sx={{ mb: 4, fontWeight: 600 }}>
        Comentarii ({comments.length})
      </Typography>
      {/* Comments List */}
      {comments.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          {comments.map((comment) => (
            <Paper
              key={comment.id}
              elevation={1}
              sx={{
                p: 3,
                mb: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 40,
                    height: 40,
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {comment.authorName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {comment.authorName}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>
                      <DateTimeRelative date={comment.createdAt} />
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                  >
                    {comment.content}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
            textAlign: "center",
            py: 4
          }}>
          Nu există comentarii încă. Fii primul care comentează!
        </Typography>
      )}
      <Divider sx={{ my: 4 }} />
      {/* Comment Form */}
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Lasă un comentariu
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Name field - show for everyone, but hide if logged in user has a name */}
            {(!session?.user || !session.user.name) && (
              <TextField
                fullWidth
                label="Nume"
                value={formData.authorName}
                onChange={handleInputChange("authorName")}
                required
                disabled={submitting}
                sx={{ flex: 1 }}
              />
            )}

            {/* Email field - hide for logged-in users */}
            {!session?.user && (
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.authorEmail}
                onChange={handleInputChange("authorEmail")}
                disabled={submitting}
                sx={{ flex: 1 }}
              />
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comentariu"
            value={formData.content}
            onChange={handleInputChange("content")}
            required
            disabled={submitting}
            helperText={`${formData.content.length}/2000 caractere`}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                "Trimite comentariul"
              )}
            </Button>
          </Box>
        </Box>

        {/* Moderation message - only show for non-logged-in users */}
        {!session?.user && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 2
            }}>
            Comentariile vor fi publicate după moderare. * Câmpuri obligatorii.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
